/**
 * Multiple Choice Parser
 *
 * Parses message content to extract <multiple-choice> blocks
 * and returns structured segments for rendering.
 */

import { MultipleChoiceData } from "../../DocumentAgentLG/DocumentAgentLGTypes";

// ============================================================================
// TYPES
// ============================================================================

export interface TextSegment {
  type: "text";
  content: string;
}

export interface ChoiceSegment {
  type: "choice";
  content: MultipleChoiceData;
}

export type ParsedSegment = TextSegment | ChoiceSegment;

// ============================================================================
// PARSER FUNCTIONS
// ============================================================================

/**
 * Check if a content string has complete multiple choice blocks.
 * Used during streaming to avoid parsing incomplete JSON.
 *
 * @param content - The message content to check
 * @returns true if all opened <multiple-choice> tags have closing tags
 */
export function isCompleteChoiceBlock(content: string): boolean {
  const openTags = (content.match(/<multiple-choice>/g) || []).length;
  const closeTags = (content.match(/<\/multiple-choice>/g) || []).length;
  return openTags === closeTags;
}

/**
 * Check if content contains any multiple choice blocks (complete or incomplete)
 *
 * @param content - The message content to check
 * @returns true if content contains <multiple-choice> tag
 */
export function hasMultipleChoiceBlock(content: string): boolean {
  return content.includes("<multiple-choice>");
}

/**
 * Parse message content and extract multiple choice blocks.
 *
 * @param content - The message content to parse
 * @returns Array of parsed segments (text and choice blocks)
 */
export function parseMultipleChoice(content: string): ParsedSegment[] {
  // If no complete choice blocks, return as single text segment
  if (!hasMultipleChoiceBlock(content) || !isCompleteChoiceBlock(content)) {
    return [{ type: "text", content }];
  }

  const segments: ParsedSegment[] = [];
  const regex = /<multiple-choice>([\s\S]*?)<\/multiple-choice>/g;

  let lastIndex = 0;
  let match;

  while ((match = regex.exec(content)) !== null) {
    // Add text before this match
    if (match.index > lastIndex) {
      const textContent = content.slice(lastIndex, match.index).trim();
      if (textContent) {
        segments.push({ type: "text", content: textContent });
      }
    }

    // Parse the JSON content inside the tags
    const jsonString = match[1].trim();
    try {
      const choiceData = JSON.parse(jsonString) as MultipleChoiceData;

      // Validate required fields
      if (
        choiceData.id &&
        choiceData.question &&
        Array.isArray(choiceData.options)
      ) {
        segments.push({ type: "choice", content: choiceData });
      } else {
        // Invalid structure, treat as text
        segments.push({ type: "text", content: match[0] });
      }
    } catch (e) {
      // JSON parse error, treat as text
      segments.push({ type: "text", content: match[0] });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < content.length) {
    const textContent = content.slice(lastIndex).trim();
    if (textContent) {
      segments.push({ type: "text", content: textContent });
    }
  }

  // If no segments were created (shouldn't happen), return original content
  if (segments.length === 0) {
    return [{ type: "text", content }];
  }

  return segments;
}

/**
 * Extract all choice IDs from message content.
 * Used to determine which choices have been answered.
 *
 * @param content - The message content to parse
 * @returns Array of choice IDs found in the content
 */
export function extractChoiceIds(content: string): string[] {
  if (!hasMultipleChoiceBlock(content) || !isCompleteChoiceBlock(content)) {
    return [];
  }

  const ids: string[] = [];
  const regex = /<multiple-choice>([\s\S]*?)<\/multiple-choice>/g;
  let match;

  while ((match = regex.exec(content)) !== null) {
    try {
      const choiceData = JSON.parse(match[1].trim()) as MultipleChoiceData;
      if (choiceData.id) {
        ids.push(choiceData.id);
      }
    } catch {
      // Ignore parse errors
    }
  }

  return ids;
}

/**
 * Check if a user message matches any option label from a choice.
 * Used to determine if a choice has been answered.
 *
 * @param userMessage - The user's message content
 * @param choiceData - The multiple choice data
 * @returns true if the user message matches an option or could be a custom answer
 */
export function isChoiceAnswer(
  userMessage: string,
  choiceData: MultipleChoiceData
): boolean {
  const trimmedMessage = userMessage.trim();

  // Check if message matches any option label exactly
  for (const option of choiceData.options) {
    if (option.label === trimmedMessage) {
      return true;
    }
  }

  // Check for multi-select format: "Option A, Option B"
  const selectedLabels = trimmedMessage.split(",").map(s => s.trim());
  const allMatch = selectedLabels.every(label =>
    choiceData.options.some(opt => opt.label === label)
  );
  if (allMatch && selectedLabels.length > 0) {
    return true;
  }

  // If allowCustom is true, any short message could be a custom answer
  // We'll be conservative and only match exact options
  return false;
}
